export const DEFAULT_PALMARES_IMAGE = "/img/cover/cover_palmares.webp";

export const DEFAULT_PALMARES_FILTERS = {
  search: "",
  rank: "all",
  year: "all",
  document: "all",
  sort: "rank",
};

export const PALMARES_RANK_OPTIONS = [
  "1èr(e)",
  "2ème",
  "3ème",
  "4ème",
  "5ème",
  "7ème",
  "9ème",
  "N.C.",
];

export const RESULT_TOKENS = [
  {
    value: "✅",
    label: "Gagné",
    className:
      "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200",
  },
  {
    value: "❌",
    label: "Perdu",
    className:
      "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200",
  },
  {
    value: "N",
    label: "Nul",
    className:
      "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70",
  },
  {
    value: "F",
    label: "Forfait",
    className:
      "bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200",
  },
  {
    value: "D",
    label: "Disqualifié",
    className:
      "bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-200",
  },
];

export function normalizeAdherentsResponse(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.adherents)) return response.adherents;
  if (Array.isArray(response?.Adherents)) return response.Adherents;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.adherents)) return response.data.adherents;
  return [];
}

export function normalizePalmaresResponse(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.palmares)) return response.palmares;
  if (Array.isArray(response?.Palmares)) return response.Palmares;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.palmares)) return response.data.palmares;
  if (Array.isArray(response?.data?.Palmares)) return response.data.Palmares;
  return [];
}

export function safeDate(value) {
  if (!value) return null;

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function getPalmaresKey(item, index = "") {
  if (item?.id) return `palmares-${item.id}`;

  if (item?.event_palmares_id) {
    return `palmares-event-${item.event_palmares_id}-${index}`;
  }

  return `palmares-fallback-${index}`;
}

export function dedupePalmaresItems(items = []) {
  const seen = new Set();

  return items.filter((item, index) => {
    const key = getPalmaresKey(item, index);

    if (seen.has(key)) {
      console.warn("[PalmaresPage] Palmarès dupliqué ignoré :", key, item);
      return false;
    }

    seen.add(key);
    return true;
  });
}

export function formatPalmaresDate(value) {
  const date = safeDate(value);
  if (!date) return "Date non renseignée";

  return new Intl.DateTimeFormat("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatPalmaresShortDate(value) {
  const date = safeDate(value);
  if (!date) return "--/--/----";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function getPalmaresYear(value) {
  const date = safeDate(value);
  if (!date) return "";

  return String(date.getFullYear());
}

export function getNormalizedRank(rank) {
  const value = normalizeText(rank).replace(/\s/g, "");

  if (value.startsWith("1")) return "first";
  if (value.startsWith("2")) return "second";
  if (value.startsWith("3")) return "third";

  return "other";
}

export function getRankWeight(rank) {
  const group = getNormalizedRank(rank);

  if (group === "first") return 1;
  if (group === "second") return 2;
  if (group === "third") return 3;

  const value = normalizeText(rank);

  if (value.startsWith("4")) return 4;
  if (value.startsWith("5")) return 5;
  if (value.startsWith("7")) return 7;
  if (value.startsWith("9")) return 9;

  return 99;
}

export function getRankLabel(rank) {
  const group = getNormalizedRank(rank);

  if (group === "first") return "Or";
  if (group === "second") return "Argent";
  if (group === "third") return "Bronze";

  return rank || "Autre";
}

export function getRankTitle(rank) {
  const group = getNormalizedRank(rank);

  if (group === "first") return "1ère place";
  if (group === "second") return "2ème place";
  if (group === "third") return "3ème place";

  return "Autres places";
}

export function getRankMedalImage(rank) {
  const group = getNormalizedRank(rank);

  if (group === "first") return "/img/palmares/1er-prix.png";
  if (group === "second") return "/img/palmares/2eme-prix.png";
  if (group === "third") return "/img/palmares/3eme-prix.png";

  return null;
}

export function getRankGradient(rank) {
  const group = getNormalizedRank(rank);

  if (group === "first") {
    return "from-amber-300 via-yellow-500 to-orange-600";
  }

  if (group === "second") {
    return "from-slate-200 via-slate-400 to-slate-600";
  }

  if (group === "third") {
    return "from-orange-300 via-amber-700 to-orange-950";
  }

  return "from-sky-500 via-blue-700 to-slate-950";
}

export function getRankBadgeClass(rank) {
  const group = getNormalizedRank(rank);

  if (group === "first") {
    return "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-200";
  }

  if (group === "second") {
    return "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/75";
  }

  if (group === "third") {
    return "bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200";
  }

  return "bg-sky-100 text-sky-700 dark:bg-sky-500/10 dark:text-sky-200";
}

export function parseResultTokens(resultats = "") {
  return String(resultats || "")
    .split("")
    .filter(Boolean);
}

export function getResultTokenLabel(token) {
  const match = RESULT_TOKENS.find((item) => item.value === token);
  return match?.label || token;
}

export function getResultTokenClass(token) {
  const match = RESULT_TOKENS.find((item) => item.value === token);

  return (
    match?.className ||
    "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70"
  );
}

export function hasPalmaresDocument(item) {
  return Boolean(item?.podium_palmares || item?.tableau_palmares);
}

export function sortPalmaresItems(items = [], sort = "rank") {
  const sorted = [...items];

  if (sort === "dateDesc") {
    return sorted.sort((a, b) => {
      const dateA = safeDate(a?.date_palmares)?.getTime() || 0;
      const dateB = safeDate(b?.date_palmares)?.getTime() || 0;
      return dateB - dateA;
    });
  }

  if (sort === "dateAsc") {
    return sorted.sort((a, b) => {
      const dateA = safeDate(a?.date_palmares)?.getTime() || 0;
      const dateB = safeDate(b?.date_palmares)?.getTime() || 0;
      return dateA - dateB;
    });
  }

  if (sort === "titleAsc") {
    return sorted.sort((a, b) =>
      String(a?.titre_palmares || "").localeCompare(
        String(b?.titre_palmares || ""),
        "fr",
      ),
    );
  }

  return sorted.sort((a, b) => {
    const rankA = getRankWeight(a?.classement_palmares);
    const rankB = getRankWeight(b?.classement_palmares);

    if (rankA !== rankB) return rankA - rankB;

    const dateA = safeDate(a?.date_palmares)?.getTime() || 0;
    const dateB = safeDate(b?.date_palmares)?.getTime() || 0;

    return dateB - dateA;
  });
}

export function filterPalmaresItems(
  items = [],
  filters = DEFAULT_PALMARES_FILTERS,
) {
  const search = normalizeText(filters.search);

  const filtered = items.filter((item) => {
    const rankGroup = getNormalizedRank(item?.classement_palmares);
    const itemYear = getPalmaresYear(item?.date_palmares);
    const hasDocument = hasPalmaresDocument(item);

    const searchableContent = normalizeText(
      [
        item?.titre_palmares,
        item?.classement_palmares,
        item?.resultats_palmares,
        item?.observations_palmares,
        itemYear,
      ].join(" "),
    );

    const matchesSearch = !search || searchableContent.includes(search);

    const matchesRank =
      filters.rank === "all" ||
      filters.rank === rankGroup ||
      item?.classement_palmares === filters.rank;

    const matchesYear = filters.year === "all" || filters.year === itemYear;

    const matchesDocument =
      filters.document === "all" ||
      (filters.document === "with" && hasDocument) ||
      (filters.document === "without" && !hasDocument);

    return matchesSearch && matchesRank && matchesYear && matchesDocument;
  });

  return sortPalmaresItems(filtered, filters.sort);
}

export function countActivePalmaresFilters(
  filters = DEFAULT_PALMARES_FILTERS,
) {
  return Object.entries(filters).reduce((count, [key, value]) => {
    if (key === "rank" && value === "all") return count;
    if (key === "year" && value === "all") return count;
    if (key === "document" && value === "all") return count;
    if (key === "sort" && value === "rank") return count;
    if (!value) return count;

    return count + 1;
  }, 0);
}

export function getPalmaresYears(items = []) {
  return Array.from(
    new Set(
      items
        .map((item) => getPalmaresYear(item?.date_palmares))
        .filter(Boolean),
    ),
  ).sort((a, b) => Number(b) - Number(a));
}

export function getPalmaresStats(items = []) {
  const first = items.filter(
    (item) => getNormalizedRank(item?.classement_palmares) === "first",
  ).length;

  const second = items.filter(
    (item) => getNormalizedRank(item?.classement_palmares) === "second",
  ).length;

  const third = items.filter(
    (item) => getNormalizedRank(item?.classement_palmares) === "third",
  ).length;

  const podiums = first + second + third;
  const documents = items.filter(hasPalmaresDocument).length;
  const years = getPalmaresYears(items);

  return {
    total: items.length,
    first,
    second,
    third,
    podiums,
    other: Math.max(items.length - podiums, 0),
    documents,
    yearsCount: years.length,
    bestYear: years[0] || "—",
  };
}